import React, { SyntheticEvent } from 'react';
import { FormattedMessage } from 'react-intl';
import DefaultButton from '../../../../global/default-button/default-button';
import { Space } from '../../../../../constants/html-character-entities';
import { DefaultCloseModalButton } from '../../../../global/modal/common/common-close-modal-buttons';
import { CommonModal } from '../../../../global/modal/common/common-modal';
import { ConnectionType, IConnectionDetailsDTO, IDeleteConnectionRequestParams } from '../../../../../api/connections.dao';
import { alertAudioBridgeConnectionMessage, alertMessage, audioBrdigeWarningMessage } from '../../../../../constants/connections-labels.constants';
import { DELETE_THIS_CONNECTION_TEXT } from '../../_connections/connections.texts';
import { alertPanelistLinkConnectionMessage, panelistLinkWarningMessage } from '../../../../../constants/panelist-link-connection.constants';


export interface IDeleteConnectionDetails {
    isConnectionAutoSync: boolean | undefined;
    type: ConnectionType;
}
interface IProps {
    handleOnDelete: (data: IDeleteConnectionRequestParams, connectionDetails: IDeleteConnectionDetails) => void;
    deletePopoverConnection: IConnectionDetailsDTO | null;
    setDeletePopoverConnection: (connection: IConnectionDetailsDTO | null) => void;
}

interface IAlertBodyMessageProps {
    isZoomWebinarAudioBridgeConnection: boolean;
    deleteConnectionType: ConnectionType;
}

export const ZoomDeleteBodyMessage = () => (
    <div className='alert-body'>
        <span className='alert-message'>
            <FormattedMessage
                id='oe.connections.alerts.areYouSure'
                description='Alert message'
                defaultMessage='Are you sure you want to '
            />
            <Space/>
            <span className='alert-message--danger'>
                <FormattedMessage
                    id='oe.connections.alerts.deleteThisZoomConnection'
                    description='Alert message second part'
                    defaultMessage=' delete this Zoom connection'
                />
            </span>
            ?
        </span>
        <br/>
        <span className='alert-message'>
            <FormattedMessage
                id='oe.connections.alerts.deleteBothTheZoomMeetingAndTheConnectionOrDeleteOnlyTheConnectionWillBeDeletedAndUnassigned'
                description='Warning message'
                defaultMessage='You can choose to permanently delete both the Zoom Meeting and the Connection, or to delete only the Connection and unassign it from all attendees.'
            />
        </span>
    </div>
);

export const WebinarDeleteBodyMessage = () => (
    <div className='alert-body'>
        <span className='alert-message'>
            <FormattedMessage
                id='oe.connections.alerts.areYouSure'
                description='Alert message'
                defaultMessage='Are you sure you want to '
            />
            <Space/>
            <span className='alert-message--danger'>
                <FormattedMessage
                    id='oe.connections.alerts.deleteThisZoomWebinarConnection'
                    description='Alert message second part'
                    defaultMessage=' delete this Zoom Webinar connection'
                />
            </span>
            ?
        </span>
        <br/>
        <span className='alert-message'>
            <FormattedMessage
                id='oe.connections.alerts.deleteBothTheZoomWebinarMeetingAndTheConnectionOrDeleteOnlyTheConnectionWillBeDeletedAndUnassigned'
                description='Warning message'
                defaultMessage='You can choose to permanently delete both the Zoom Webinar and the Connection, or to delete only the Connection and unassign it from all attendees.'
            />
        </span>
    </div>
);

export const DefaultBodyMessage = () => (
    <div className='alert-body'>
        <span className='alert-message'>
            <FormattedMessage
                id='oe.connections.alerts.areYouSure'
                description='Alert message'
                defaultMessage='Are you sure you want to '
            />
            <Space/>
            <span className='alert-message--danger'>
                <FormattedMessage {...DELETE_THIS_CONNECTION_TEXT} />
            </span>
            ?
        </span>
        <br/>
        <span className='alert-message'>
            <FormattedMessage
                id='oe.connections.alerts.connectionWillBeUnassigned'
                description='Warning message'
                defaultMessage='Connection will be unassigned from all attendees.'
            />
        </span>
    </div>
);

export const AudioBridgeDeleteBodyMessage = () => (
    <div className='alert-body'>
        <span className='alert-message'>
            {alertMessage}
            <Space/>
            <span className='alert-message--danger'>{alertAudioBridgeConnectionMessage}</span>
            ?
        </span>
        <br/>
        <span className='alert-message'>{audioBrdigeWarningMessage}</span>
    </div>
);

export const PanelistLinkDeleteBodyMessage = () => (
    <div className='alert-body'>
        <span className='alert-message'>
            {alertMessage}
            <Space/>
            <span className='alert-message--danger'>{alertPanelistLinkConnectionMessage}</span>
            ?
        </span>
        <br/>
        <span className='alert-message'>{panelistLinkWarningMessage}</span>
    </div>
);

const AlertBodyMessage = ({ deleteConnectionType, isZoomWebinarAudioBridgeConnection }: IAlertBodyMessageProps) => {

    const bodyBodyMessage = {
        [ConnectionType.Zoom]: <ZoomDeleteBodyMessage />,
        [ConnectionType.Webinar]: <WebinarDeleteBodyMessage />,
        [ConnectionType.AudioBridge]: <AudioBridgeDeleteBodyMessage />,
        [ConnectionType.PanelistLink]: <PanelistLinkDeleteBodyMessage />,
    };

    return !isZoomWebinarAudioBridgeConnection
        ? <DefaultBodyMessage />
        : bodyBodyMessage[deleteConnectionType];
};

interface IFootersButtons {
    showCustomFooter: boolean;
    handleOnDelete: (data: IDeleteConnectionRequestParams, connectionDetails: IDeleteConnectionDetails) => void;
    deletePopoverConnection: IConnectionDetailsDTO | null;
    setDeletePopoverConnection: (connection: IConnectionDetailsDTO | null) => void;
    closeDeleteButtonPopover: (e?: SyntheticEvent) => void;
}

const FootersButtons = ({
    handleOnDelete,
    deletePopoverConnection,
    showCustomFooter,
    closeDeleteButtonPopover
}: IFootersButtons) => {
    const isConnectionExist = !!deletePopoverConnection;
    if (!isConnectionExist) return null;
    const {
        type,
        meetingConnectionId,
        autoSyncEnabled,
        joinMethodId,
        parentMeetingConnectionId,
        connectionVersion
    } = deletePopoverConnection;

    const isPanelistLink = type === ConnectionType.PanelistLink;

    return (
        <div className={`buttons-block ${!showCustomFooter ? 'buttons-block--from-right' : 'buttons-block--space-between'}`}>
            {
                !showCustomFooter
                    ? (
                        <DefaultButton
                            modifier={['fat', 'confirm-warn']}
                            text={
                                <FormattedMessage
                                    id='oe.connections.delete.button'
                                    description='Delete connection button'
                                    defaultMessage='Delete Connection'
                                />
                            }
                            onClick={(e: SyntheticEvent) => {
                                e.stopPropagation();
                                handleOnDelete(
                                    {
                                        meetingConnectionId: meetingConnectionId,
                                        zoomMeetingDelete: false,
                                        webinarMeetingDelete: false,
                                        audioBridgeMeetingDelete: false,
                                        panelistMeetingDelete: false,
                                        isPanelistLink: isPanelistLink,
                                        joinMethodId: joinMethodId,
                                        parentMeetingConnectionId: parentMeetingConnectionId,
                                        connectionVersion: connectionVersion
                                    },
                                    {
                                        isConnectionAutoSync: autoSyncEnabled,
                                        type: type
                                    }
                                );
                                closeDeleteButtonPopover();
                            }}
                        />
                    ) : (
                        <div className='buttons-block buttons-block--from-right'>
                            <DefaultButton
                                modifier={['fat', 'confirm-warn']}
                                text={
                                    <FormattedMessage
                                        id='oe.connections.delete.both.button'
                                        description='Delete connection button'
                                        defaultMessage='Delete Both'
                                    />
                                }
                                onClick={(e: SyntheticEvent) => {
                                    e.stopPropagation();
                                    handleOnDelete(
                                        {
                                            meetingConnectionId: meetingConnectionId,
                                            zoomMeetingDelete: type === ConnectionType.Zoom ? true : false,
                                            webinarMeetingDelete: type === ConnectionType.Webinar ? true : false,
                                            audioBridgeMeetingDelete: type === ConnectionType.AudioBridge ? true : false,
                                            panelistMeetingDelete: type === ConnectionType.PanelistLink ? true : false,
                                            isPanelistLink:  isPanelistLink,
                                            joinMethodId: joinMethodId,
                                            parentMeetingConnectionId: parentMeetingConnectionId,
                                            connectionVersion: connectionVersion
                                        },
                                        {
                                            isConnectionAutoSync: autoSyncEnabled,
                                            type: type
                                        }
                                    );
                                    closeDeleteButtonPopover();
                                }}
                            />
                            <DefaultButton
                                modifier={['fat', 'confirm-warn']}
                                text={
                                    <FormattedMessage
                                        id='oe.connection.only.delete.button'
                                        description='Delete connection button'
                                        defaultMessage='Delete Connection only'
                                    />
                                }
                                onClick={(e: SyntheticEvent) => {
                                    e.stopPropagation();
                                    handleOnDelete(
                                        {
                                            meetingConnectionId: meetingConnectionId,
                                            zoomMeetingDelete: false,
                                            webinarMeetingDelete: false,
                                            audioBridgeMeetingDelete: false,
                                            isPanelistLink: isPanelistLink,
                                            joinMethodId: joinMethodId,
                                            parentMeetingConnectionId: parentMeetingConnectionId,
                                            connectionVersion: connectionVersion
                                        },
                                        {
                                            isConnectionAutoSync: autoSyncEnabled,
                                            type: type
                                        }
                                    );
                                    closeDeleteButtonPopover();
                                }}
                            />
                        </div>
                    )
            }
            <DefaultCloseModalButton/>
        </div>
    );
};

const ConfirmDeletePopover = (props: IProps) => {
    const isZoomConnection = props.deletePopoverConnection?.type === ConnectionType.Zoom;
    const isWebinarConnection = props.deletePopoverConnection?.type === ConnectionType.Webinar;
    const isAudioBridgeConnection = props.deletePopoverConnection?.type === ConnectionType.AudioBridge;
    const isPanelistLinkConnection = props.deletePopoverConnection?.type === ConnectionType.PanelistLink;
    const isZoomWebinarAudioBridgeConnection = [isZoomConnection, isWebinarConnection, isAudioBridgeConnection, isPanelistLinkConnection].some(Boolean);

    const closeDeleteButtonPopover = (e?: SyntheticEvent) => {
        e?.stopPropagation();
        props.setDeletePopoverConnection(null);
    };
    return (<>
        <CommonModal
            isOpen={!!props.deletePopoverConnection}
            onRequestClose={closeDeleteButtonPopover}
            supressRenderingCrossIcon={true}
            footer={
                <FootersButtons
                    {...props}
                    showCustomFooter={isZoomWebinarAudioBridgeConnection}
                    closeDeleteButtonPopover={closeDeleteButtonPopover}
                />

            }
        >
            <AlertBodyMessage
                isZoomWebinarAudioBridgeConnection={isZoomWebinarAudioBridgeConnection}
                deleteConnectionType={props.deletePopoverConnection?.type}
            />
        </CommonModal>
    </>);
};

export { ConfirmDeletePopover, AlertBodyMessage };
